import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { SlidersHorizontal, X, Search, ChevronDown } from "lucide-react";
import { Link } from "react-router-dom";
import { api } from "../api";
import ProductCard from "../components/ProductCard";
import { CATEGORY_TAXONOMY, categoryPath } from "../taxonomy";

const SORT_OPTIONS = [
  { value: "featured", label: "Featured" },
  { value: "newest", label: "Newest" },
  { value: "price_asc", label: "Price · Low to high" },
  { value: "price_desc", label: "Price · High to low" },
  { value: "name", label: "Alphabetical" },
];

export default function Shop() {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [facets, setFacets] = useState({ sizes: [], colors: [], price_min: 0, price_max: 0 });
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [openCategory, setOpenCategory] = useState("");

  const category = searchParams.get("category") || "";
  const subcategory = searchParams.get("subcategory") || "";
  const search = searchParams.get("search") || "";
  const sortBy = searchParams.get("sort") || "featured";
  const sizesParam = searchParams.get("sizes") || "";
  const colorsParam = searchParams.get("colors") || "";
  const sizes = useMemo(() => sizesParam.split(",").filter(Boolean), [sizesParam]);
  const colors = useMemo(() => colorsParam.split(",").filter(Boolean), [colorsParam]);
  const priceMin = searchParams.get("price_min") || "";
  const priceMax = searchParams.get("price_max") || "";
  const featured = searchParams.get("featured") === "true";
  const newArrival = searchParams.get("new_arrival") === "true";
  const shopCategories = categories.length ? categories : CATEGORY_TAXONOMY;

  const updateParam = useCallback(
    (patch) => {
      const next = new URLSearchParams(searchParams);
      Object.entries(patch).forEach(([k, v]) => {
        if (v === null || v === "" || v === false || (Array.isArray(v) && v.length === 0)) {
          next.delete(k);
        } else {
          next.set(k, Array.isArray(v) ? v.join(",") : String(v));
        }
      });
      setSearchParams(next);
    },
    [searchParams, setSearchParams],
  );

  useEffect(() => {
    (async () => {
      try {
        const [c, f] = await Promise.all([api.get("/api/categories/tree"), api.get("/api/products/facets")]);
        setCategories(c.data);
        setFacets(f.data);
      } catch {}
    })();
  }, []);

  useEffect(() => {
    (async () => {
      setLoading(true);
      setLoadError(false);
      try {
        const params = { limit: 200, sort_by: sortBy };
        if (category) params.category = category;
        if (subcategory) params.subcategory = subcategory;
        if (search) params.search = search;
        if (sizes.length) params.sizes = sizes.join(",");
        if (colors.length) params.colors = colors.join(",");
        if (priceMin) params.price_min = priceMin;
        if (priceMax) params.price_max = priceMax;
        if (featured) params.featured = true;
        if (newArrival) params.new_arrival = true;
        const { data } = await api.get("/api/products", { params });
        setProducts(data);
      } catch {
        setLoadError(true);
      } finally {
        setLoading(false);
      }
    })();
  }, [category, subcategory, search, sortBy, sizes, colors, priceMin, priceMax, featured, newArrival]);

  const toggleArray = (list, val) => (list.includes(val) ? list.filter((x) => x !== val) : [...list, val]);

  const activeFilterCount =
    (category ? 1 : 0) +
    (subcategory ? 1 : 0) +
    (sizes.length > 0 ? 1 : 0) +
    (colors.length > 0 ? 1 : 0) +
    (priceMin || priceMax ? 1 : 0) +
    (featured ? 1 : 0) +
    (newArrival ? 1 : 0);

  return (
    <main className="page" data-testid="shop-page">
      <div className="page-head">
        <h1>
          The <em>collection.</em>
        </h1>
        <p>{products.length ? `A curated edit of ${products.length} looks.` : "Every expression of occasion, colour and craft."}</p>
      </div>

      <nav className="shop-category-nav" aria-label="Shop categories">
        {shopCategories.map((item) => (
          <div className={`shop-category-item ${openCategory === item.slug ? "is-open" : ""}`} key={item.slug}>
            <button
              type="button"
              className="shop-category-main"
              aria-expanded={openCategory === item.slug}
              onClick={() => setOpenCategory((current) => current === item.slug ? "" : item.slug)}
            >
              <span>{item.name}</span><ChevronDown size={14} aria-hidden="true" />
            </button>
            <div className="shop-category-dropdown">
              <Link to={categoryPath(item)} className="shop-category-all">Shop all {item.name}</Link>
              {item.children.map((child) => (
                <Link key={child.slug} to={categoryPath(item, child)}>{child.name}</Link>
              ))}
            </div>
          </div>
        ))}
      </nav>

      <div className="shop-toolbar">
        <div className="left">
          <button className="filter-trigger" onClick={() => setDrawerOpen(true)} data-testid="filter-drawer-trigger">
            <SlidersHorizontal size={14} />
            Filters {activeFilterCount > 0 && `(${activeFilterCount})`}
          </button>
          <span className="result-count" data-testid="result-count">
            {loading ? "Loading…" : `${products.length} looks`}
          </span>
        </div>
        <div className="right">
          <label style={{ display: "inline-flex", alignItems: "center", gap: 8, borderBottom: "1px solid var(--line)", padding: "4px 0" }}>
            <Search size={13} />
            <input
              type="text"
              placeholder="Search"
              defaultValue={search}
              onKeyDown={(e) => {
                if (e.key === "Enter") updateParam({ search: e.currentTarget.value });
              }}
              style={{ border: 0, background: "transparent", outline: 0, fontSize: 12, letterSpacing: "0.14em", textTransform: "uppercase", width: 140 }}
              data-testid="shop-search-input"
            />
          </label>
          <select className="sort-select" value={sortBy} onChange={(e) => updateParam({ sort: e.target.value })} data-testid="shop-sort-select">
            {SORT_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                Sort — {o.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      {loading ? (
        <p className="empty" data-testid="shop-loading">Waking our atelier and loading products...</p>
      ) : loadError ? (
        <div className="category-empty-state" data-testid="shop-load-error">
          <h2>Still connecting.</h2>
          <p>The collection is taking longer than expected.</p>
          <button className="btn btn-outline" onClick={() => window.location.reload()}>Try again</button>
        </div>
      ) : products.length ? (
        <div className="product-grid" data-testid="shop-grid">
          {products.map((p) => (
            <ProductCard product={p} key={p.id} />
          ))}
        </div>
      ) : (
        <p className="empty" data-testid="empty-search-state">No looks match this filter — try widening the search.</p>
      )}

      {/* Filter drawer */}
      <div className={`drawer-backdrop ${drawerOpen ? "open" : ""}`} onClick={() => setDrawerOpen(false)} />
      <aside className={`filter-drawer ${drawerOpen ? "open" : ""}`} data-testid="filter-drawer">
        <div className="drawer-head">
          <h3>Filter looks</h3>
          <button onClick={() => setDrawerOpen(false)} data-testid="filter-drawer-close" aria-label="Close">
            <X size={20} />
          </button>
        </div>
        <div className="drawer-body">
          <div className="filter-group">
            <h4>Category</h4>
            <div className="filter-chips">
              <button className={`filter-chip ${!category ? "active" : ""}`} onClick={() => updateParam({ category: "" })} data-testid="filter-all-button">
                All
              </button>
              {categories.map((c) => (
                <div className="filter-taxonomy-group" key={c.id}>
                  <button
                    className={`filter-chip ${category === c.slug && !subcategory ? "active" : ""}`}
                    onClick={() => updateParam({ category: c.slug, subcategory: "" })}
                    data-testid={`filter-category-${c.slug}-button`}
                  >{c.name}</button>
                  <div className="filter-subcategories">
                    {(c.children || []).map((child) => (
                      <button key={child.id} className={`filter-chip ${category === c.slug && subcategory === child.slug ? "active" : ""}`}
                        onClick={() => updateParam({ category: c.slug, subcategory: child.slug })}>
                        {child.name}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {facets.sizes.length > 0 && (
            <div className="filter-group">
              <h4>Size</h4>
              <div className="filter-chips">
                {facets.sizes.map((s) => (
                  <button
                    key={s}
                    className={`filter-chip ${sizes.includes(s) ? "active" : ""}`}
                    onClick={() => updateParam({ sizes: toggleArray(sizes, s) })}
                    data-testid={`filter-size-${s.toLowerCase()}-button`}
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>
          )}

          {facets.colors.length > 0 && (
            <div className="filter-group">
              <h4>Colour</h4>
              <div className="filter-chips">
                {facets.colors.map((c) => (
                  <button
                    key={c}
                    className={`filter-chip ${colors.includes(c) ? "active" : ""}`}
                    onClick={() => updateParam({ colors: toggleArray(colors, c) })}
                    data-testid={`filter-color-${c.toLowerCase().replace(/\s+/g, "-")}-button`}
                  >
                    {c}
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="filter-group">
            <h4>Price (₹)</h4>
            <div className="price-range">
              <input
                type="number"
                placeholder={`Min ${facets.price_min || 0}`}
                value={priceMin}
                onChange={(e) => updateParam({ price_min: e.target.value })}
                data-testid="filter-price-min"
              />
              <span>—</span>
              <input
                type="number"
                placeholder={`Max ${facets.price_max || 0}`}
                value={priceMax}
                onChange={(e) => updateParam({ price_max: e.target.value })}
                data-testid="filter-price-max"
              />
            </div>
          </div>

          <div className="filter-group">
            <h4>Highlights</h4>
            <div className="filter-chips">
              <button className={`filter-chip ${featured ? "active" : ""}`} onClick={() => updateParam({ featured: featured ? "" : "true" })} data-testid="filter-featured">
                Featured
              </button>
              <button className={`filter-chip ${newArrival ? "active" : ""}`} onClick={() => updateParam({ new_arrival: newArrival ? "" : "true" })} data-testid="filter-new-arrival">
                New arrival
              </button>
            </div>
          </div>
        </div>
        <div className="drawer-foot">
          <button
            className="btn btn-outline"
            onClick={() => {
              setSearchParams(new URLSearchParams());
            }}
            data-testid="filter-clear-all"
          >
            Clear all
          </button>
          <button className="btn btn-primary" onClick={() => setDrawerOpen(false)} data-testid="filter-apply">
            Apply
          </button>
        </div>
      </aside>
    </main>
  );
}
